import React, { TdHTMLAttributes, ThHTMLAttributes } from "react"

import { classNames } from "../../../../lib/classNames"

export const TableCellWidth = {
  ExtraExtraSmall: "xxs",
  ExtraSmall: "xs",
  Small: "sm",
  Medium: "md",
  Large: "lg",
  ExtraLarge: "xl",
  ExtraExtraLarge: "xxl",
  ExtraExtraExtraLarge: "xxxl",
  Full: "full",
} as const

export type TableCellWidth =
  (typeof TableCellWidth)[keyof typeof TableCellWidth]

export const TABLE_CELL_BASE_STYLES = [
  "bg-input-default px-2.5 align-middle",
  "border-v2-border-default border-r",
  "truncate text-left text-xs",
] as const

export const TableCellHeight = {
  Small: "sm",
  Large: "lg",
} as const
export type TableCellHeight =
  (typeof TableCellHeight)[keyof typeof TableCellHeight]

type TableCellBaseProps = {
  height?: TableCellHeight
  as: "th" | "td"
}
export type TableCellProps = TableCellBaseProps &
  (
    | ({ as: "td" } & TdHTMLAttributes<HTMLTableCellElement>)
    | ({ as: "th" } & ThHTMLAttributes<HTMLTableCellElement>)
  ) & {
    ref?: React.Ref<HTMLTableCellElement>
    hideColumnBorders?: boolean
  }

/**
 * TableCell is a base component that enforces consistent styling across all table cells.
 * This is the lowest-level table cell component and should be used as a building block
 * by more specific table cell components. It handles core styling properties like
 * dimensions, borders, and layout that are common to both header (th) and data (td) cells.
 */
export function TableCell({
  height,
  as,
  children,
  className,
  ref,
  hideColumnBorders,
  ...otherProps
}: TableCellProps) {
  const Tag = as
  return (
    <Tag
      ref={ref}
      className={classNames(
        ...TABLE_CELL_BASE_STYLES,
        "last:border-r-0",
        hideColumnBorders && "border-none",
        // heights
        height === "sm" && "h-9", // 36px
        height === "lg" && "h-10.5", // 42px
        className
      )}
      {...otherProps}
    >
      {children}
    </Tag>
  )
}
